type ErrorPayload = {
  message?: unknown;
  error?: unknown;
  errors?: Record<string, unknown>;
};

type ApiErrorLike = {
  response?: {
    status?: number;
    data?: ErrorPayload;
  };
  code?: string;
  message?: string;
};

const exactMessages: Record<string, string> = {
  'unauthenticated.': 'Sua sessão expirou. Entre novamente para continuar.',
  unauthenticated: 'Sua sessão expirou. Entre novamente para continuar.',
  'this action is unauthorized.': 'Você não tem permissão para realizar esta ação.',
  'forbidden': 'Você não tem permissão para realizar esta ação.',
  'not found.': 'O registro solicitado não foi encontrado.',
  'server error': 'O servidor não conseguiu concluir a operação. Tente novamente e informe o horário ao suporte se persistir.',
  'internal server error': 'O servidor encontrou uma falha interna. Tente novamente e informe o horário ao suporte se persistir.',
  'invalid credentials.': 'Usuário ou senha inválidos.',
  'invalid credentials': 'Usuário ou senha inválidos.',
  'too many requests.': 'Muitas tentativas. Aguarde um instante e tente novamente.',
};

const fieldNames: Record<string, string> = {
  email: 'e-mail',
  password: 'senha',
  name: 'nome',
  phone: 'telefone',
  document: 'documento',
  'check-in': 'check-in',
  'check-out': 'check-out',
};

function fieldLabel(value: string): string {
  const normalized = value.trim().toLowerCase();
  return fieldNames[normalized] ?? normalized;
}

/**
 * A API ainda tem respostas padrão do Laravel e de integrações em inglês.
 * Esta borda garante que nenhuma dessas mensagens chegue crua à interface.
 */
export function localizeMessage(value: unknown, fallback = 'A operação não foi concluída. Revise os dados e tente novamente; se persistir, informe o horário ao suporte.'): string {
  if (typeof value !== 'string' || !value.trim()) return fallback;

  const message = value.trim();
  const exact = exactMessages[message.toLowerCase()];
  if (exact) return exact;

  const required = message.match(/^the (.+) field is required\.?$/i);
  if (required) return `O campo ${fieldLabel(required[1])} é obrigatório.`;

  const email = message.match(/^the (.+) field must be a valid email address\.?$/i);
  if (email) return `Informe um ${fieldLabel(email[1])} válido.`;

  const minimum = message.match(/^the (.+) field must be at least (\d+) characters?\.?$/i);
  if (minimum) return `O campo ${fieldLabel(minimum[1])} deve ter pelo menos ${minimum[2]} caracteres.`;

  const confirmation = message.match(/^the (.+) field confirmation does not match\.?$/i);
  if (confirmation) return `A confirmação de ${fieldLabel(confirmation[1])} não confere.`;

  // Não exibir textos técnicos de serviços externos em inglês ao hóspede/equipe.
  if (/\b(the|invalid|failed|unable|cannot|must|already|not found|server|request|resource|token|session|unexpected)\b/i.test(message)) {
    return fallback;
  }

  return message;
}

/**
 * Converte qualquer falha HTTP em uma mensagem útil para a pessoa que está
 * operando a tela, sem expor stack trace, HTML ou texto cru do provedor.
 */
export function apiErrorMessage(error: unknown, fallback?: string): string {
  const failure = error as ApiErrorLike;
  const status = failure?.response?.status;
  const payload = failure?.response?.data;
  const validation = payload?.errors
    ? Object.values(payload.errors).flatMap((value) => Array.isArray(value) ? value : [value]).filter(Boolean)
    : [];

  if (validation.length > 0) {
    return validation.map((message) => localizeMessage(message, 'Revise este campo.')).join(' ');
  }

  if (typeof payload?.message === 'string' && payload.message.trim()) {
    return localizeMessage(payload.message, fallback ?? 'A operação foi recusada. Revise os dados e tente novamente.');
  }

  if (status === 401) return 'Sua sessão expirou. Entre novamente para continuar.';
  if (status === 403) return 'Seu perfil não tem permissão para esta ação. Solicite acesso ao responsável.';
  if (status === 404) return 'O item solicitado não está mais disponível. Atualize a tela e tente novamente.';
  if (status === 409) return 'A operação entrou em conflito com o estado atual. Atualize os dados antes de continuar.';
  if (status === 422) return 'Os dados informados não passaram na validação. Revise os campos destacados.';
  if (status === 429) return 'Muitas tentativas foram realizadas. Aguarde alguns segundos antes de repetir.';
  if (status && status >= 500) return 'O servidor não conseguiu concluir esta operação. Tente novamente e, se persistir, informe o horário ao suporte.';
  if (failure.code === 'ERR_NETWORK') return 'Não foi possível alcançar o servidor. Verifique a conexão e tente novamente.';

  return localizeMessage(failure.message, fallback ?? 'A operação não foi concluída. Revise os dados e tente novamente.');
}

export function localizeErrorPayload(payload: unknown): void {
  if (!payload || typeof payload !== 'object') return;

  const data = payload as ErrorPayload;
  if (typeof data.message === 'string') data.message = localizeMessage(data.message);
  if (typeof data.error === 'string') data.error = localizeMessage(data.error);

  if (data.errors) {
    Object.entries(data.errors).forEach(([key, messages]) => {
      if (Array.isArray(messages)) {
        data.errors![key] = messages.map((message) => localizeMessage(message));
      } else {
        data.errors![key] = localizeMessage(messages);
      }
    });
  }
}
